import { Privileges } from '@reapit-sales/api-v2'
import { LegacyCRMIframeOpenPanel, PanelLegacyLink } from '@reapit-sales/legacy-crm'
import { SecondaryNavigationMenu } from '@reapit-sales/navigation'
import { trackMixpanelAction, NAVIGATION } from '@reapit-sales/utilities'
import { AddIcon } from '@reapit/elements/icons/add'

/**
 * `getQuickAdd` generates a "Quick Add" menu item with a list of sub-menu items for adding different entities.
 *
 * Props:
 * @param privileges (array): This is settings information from APIv2 - GET/configurations.
 * @param isLegacyCRMIframeLoaded (boolean): Indicates whether the legacy CRM iframe is loaded and ready to
 * receive messages.
 * @param legacyCRMIframePageName (string): The page name to send to the legacy CRM iframe for navigation
 * purposes.
 * @param legacyCRMUrl - The URL of the legacy CRM iframe, used to send the postMessage.
 *
 * Returns:
 * (SecondaryNavigationMenu): A "Quick Add" menu item with several sub-menu options (e.g., "Add Contact",
 * "Add Note", etc.), each linked to a corresponding legacy CRM panel action.
 *
 * This function:
 * 1. Creates a "Quick Add" menu item with the `add` icon.
 * 2. Adds several sub-menu items (e.g., Add Contact, Add Note) with respective onClicks that send postMessages
 * to the legacy CRM iframe.
 * 3. `hasAccess` is optional and only provide it if menu option require to be behind permission check
 */

export const getQuickAdd = (
  privileges: Privileges,
  isLegacyCRMIframeLoaded: boolean,
  legacyCRMIframePageName: string,
  legacyCRMUrl: string,
): SecondaryNavigationMenu => ({
  label: 'Quick Add',
  icon: <AddIcon />,
  onClick: () => {
    // Track the Mixpanel event for opening Quick Add Menu
    trackMixpanelAction(NAVIGATION.ACTION.OPEN_QUICK_ADD, {
      Menu: NAVIGATION.MENU_NAME.QUICK_ADD,
      FromRoute: legacyCRMIframePageName,
    })
  },
  subMenu: [
    {
      label: 'group-quick-adds',
      group: [
        {
          label: 'Add Contact',
          hasAccess: privileges.contacts?.canCreate,
          onClick: () => {
            // Track the Mixpanel event for navigating to Quick Add Contact
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_QUICK_ADD_CONTACT, {
              Panel: NAVIGATION.PANEL_NAME.QUICK_ADD_CONTACT,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.AddContact,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Add Note',
          hasAccess: privileges.notes?.canCreate,
          onClick: () => {
            // Track the Mixpanel event for navigating to Quick Add Note
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_QUICK_ADD_NOTE, {
              Panel: NAVIGATION.PANEL_NAME.QUICK_ADD_NOTE,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.AddNote,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Add Prospect / Appraisal',
          hasAccess: privileges.properties?.canCreate,
          onClick: () => {
            // Track the Mixpanel event for navigating to Quick Add Prospect / Appraisal
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_QUICK_ADD_PROSPECT_APPRAISAL, {
              Panel: NAVIGATION.PANEL_NAME.QUICK_ADD_PROSPECT_APPRAISAL,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.AddProspectAppraisal,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Log Enquiry',
          onClick: () => {
            // Track the Mixpanel event for navigating to Quick Add Log Enquiry
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_QUICK_ADD_LOG_ENQUIRY, {
              Panel: NAVIGATION.PANEL_NAME.QUICK_ADD_LOG_ENQUIRY,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.LogEnquiry,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Add Task',
          hasAccess: privileges.tasks?.canCreate,
          onClick: () => {
            // Track the Mixpanel event for navigating to Quick Add Task
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_QUICK_ADD_TASK, {
              Panel: NAVIGATION.PANEL_NAME.QUICK_ADD_TASK,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.AddTask,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Add Appointment',
          hasAccess: privileges.appointments?.canCreate,
          onClick: () => {
            // Track the Mixpanel event for navigating to Quick Add Appointment
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_QUICK_ADD_APPOINTMENT, {
              Panel: NAVIGATION.PANEL_NAME.QUICK_ADD_APPOINTMENT,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.AddAppointment,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Apply Schedule',
          hasAccess: privileges.tasks?.canCreate,
          onClick: () => {
            // Track the Mixpanel event for navigating to Quick Add Apply Schedule
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_QUICK_ADD_APPLY_SCHEDULE, {
              Panel: NAVIGATION.PANEL_NAME.QUICK_ADD_APPLY_SCHEDULE,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.ApplySchedule,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'New Referral',
          hasAccess: privileges.referrals?.canCreate,
          onClick: () => {
            // Track the Mixpanel event for navigating to Quick Add New Referral
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_QUICK_ADD_NEW_REFERRAL, {
              Panel: NAVIGATION.PANEL_NAME.QUICK_ADD_NEW_REFERRAL,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.NewReferral,
              legacyCRMUrl,
            })
          },
        },
      ],
    },
  ],
})
